import type { ClientCapabilities, ItemFace } from "@serendiptv/contracts";

/**
 * The response contract is deliberately kept structural here as the backend is
 * allowed to add diagnostics without making the player depend on them. The
 * request itself is built from the shared contracts package in api.ts.
 */
export interface SelectionResponse {
  channel: number;
  item: ItemFace & {
    description?: string | null;
    original_air_date?: string | null;
    original_station?: string | null;
    original_network?: string | null;
    creator?: string | null;
    language?: string | null;
    runtime_seconds?: number | null;
    source_collection?: string | null;
    source_provider?: string | null;
    source_identifier?: string | null;
    rights_status?: string | null;
    eligibility_status?: string | null;
  };
  playback: {
    url: string;
    mime_type?: string | null;
    start_offset_seconds?: number | null;
    duration_seconds?: number | null;
    caption_url?: string | null;
    caption_label?: string | null;
    caption_language?: string | null;
  };
  selection_id?: string | null;
  feature_flags?: Record<string, boolean>;
  station?: LiveChannelResponse["station"];
  schedule?: LiveChannelResponse["schedule"];
  broadcast?: LiveChannelResponse["broadcast"];
}

export interface StationSummary {
  id: string;
  channel: number;
  call_sign: string;
  name: string;
  identity: string;
}

export interface LiveChannelResponse {
  station: StationSummary;
  schedule: { id: string; version: number; status: string };
  content: ItemFace & {
    description: string | null;
    source_provider: string;
    source_identifier: string;
  };
  broadcast: {
    start_at: string;
    end_at: string;
    runtime_seconds: number;
    content_type: string;
    playback_offset_seconds: number;
    server_time: string;
  };
  playback: {
    url: string;
    mime_type: string | null;
    duration_seconds: number | null;
    caption_url?: string;
  };
}

export interface GuideEntry {
  station_id: string;
  channel: number;
  call_sign: string;
  schedule_version: number;
  schedule_status: string;
  content_id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  year: number | null;
  genre: string | null;
  content_type: string;
  start_at: string;
  end_at: string;
  runtime_seconds: number;
}

export interface GuideResponse {
  server_time: string;
  from: string;
  to: string;
  stations: StationSummary[];
  entries: GuideEntry[];
}

export function selectionFromLive(response: LiveChannelResponse): SelectionResponse {
  return {
    channel: response.station.channel,
    item: {
      ...response.content,
      network: response.content.network ?? response.content.station ?? "LOCAL",
      station: response.content.station,
      source_provider: response.content.source_provider,
      source_identifier: response.content.source_identifier,
      runtime_seconds: response.playback.duration_seconds ?? response.broadcast.runtime_seconds
    },
    playback: {
      url: response.playback.url,
      mime_type: response.playback.mime_type,
      start_offset_seconds: response.broadcast.playback_offset_seconds,
      duration_seconds: response.playback.duration_seconds ?? response.broadcast.runtime_seconds,
      caption_url: response.playback.caption_url
    },
    station: response.station,
    schedule: response.schedule,
    broadcast: response.broadcast
  };
}

export type Capabilities = ClientCapabilities;

export interface StoredProfile {
  viewerId: string;
  volume: number;
  muted: boolean;
  audioUnlocked: boolean;
  lastChannel?: number;
  firstSessionHintSeen: boolean;
}

export interface FaceMetadata {
  channel: number;
  title: string;
  subtitle: string | null;
  genre: string;
  year: string;
  era: string | null;
  network: string;
  station: string | null;
  item: SelectionResponse["item"] | null;
}
